/**
 * Redux slice for payment tracking and merchant reports
 * Manages payment details, analytics, and sales reports
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ApiHelperFunction } from "@funded-here-interface/common/src/Utils/HttpClient";

interface PaymentTrackingState {
  status: "idle" | "loading" | "success" | "failed";
  activePaymentDetails: any | null;
  closePaymentDetails: any | null;
  defaultPaymentDetails: any | null;
  amountDueAnalysisReport: any | null;
  allSalesReport: any | null;
  skuAndDoiReport: any | null;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}

const initialState: PaymentTrackingState = {
  status: "idle",
  activePaymentDetails: null,
  closePaymentDetails: null,
  defaultPaymentDetails: null, // Fixed: was missing from original initialState
  amountDueAnalysisReport: null,
  allSalesReport: null,
  skuAndDoiReport: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
};

/**
 * Generic API request configuration
 */
interface ApiRequestConfig {
  urlPath: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  data?: any;
  token: string;
}

/**
 * Generic async thunk factory to eliminate duplicate API call patterns
 * All thunks follow the same pattern: call API, check status, return data or show error
 */
const createApiThunk = (
  actionType: string,
  configFactory: (params: any) => ApiRequestConfig
) => {
  return createAsyncThunk(actionType, async (params: any) => {
    const config = configFactory(params);
    const response: any = await ApiHelperFunction(config);

    if (response?.status === 200) {
      return response.data;
    } else {
      const errorMessage =
        response?.response?.data?.message || "Failed to fetch merchant data";
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  });
};

/**
 * Async thunks for payment tracking operations
 */
export const getActivePayment = createApiThunk(
  "get_all_payment_merchant",
  ({ token }) => ({
    urlPath: "view-payment-tracking-byMarchent",
    method: "POST",
    token: `Bearer ${token}`,
  })
);

export const getClosePayment = createApiThunk(
  "view_close_payment_by_merchant",
  ({ token }) => ({
    urlPath: "view-payment-tracking-byMarchent",
    method: "POST",
    data: { status: "closed" },
    token: `Bearer ${token}`,
  })
);

export const getDefaultPayment = createApiThunk(
  "get_default_payment_merchant",
  ({ token }) => ({
    urlPath: "merchant-default-payment-in-merchant-summary",
    method: "GET",
    token: `Bearer ${token}`,
  })
);

export const getAmountDueAnalysis = createApiThunk(
  "get_amount_due_analysis_by_merchant",
  ({ token }) => ({
    urlPath: "ammout-due-analysis-report-byMarchant/75",
    method: "GET",
    token: token,
  })
);

export const getSkuAndDoiSales = createApiThunk(
  "get_sku_and_doi",
  ({ token }) => ({
    urlPath: "sales-comparison-report-byMarchent/75",
    method: "POST",
    token: token,
  })
);

/**
 * Helper to add standard async thunk cases (pending, fulfilled, rejected)
 * Eliminates ~90 lines of duplicate reducer logic
 */
const addAsyncThunkCases = (
  builder: any,
  thunk: any,
  stateField: keyof PaymentTrackingState
) => {
  builder
    .addCase(thunk.pending, (state: PaymentTrackingState) => {
      state.status = "loading";
      state.isLoading = true;
    })
    .addCase(
      thunk.fulfilled,
      (state: PaymentTrackingState, { payload }: any) => {
        state.status = "success";
        state.isSuccess = true;
        state.isLoading = false;
        state[stateField] = payload?.data;
      }
    )
    .addCase(thunk.rejected, (state: PaymentTrackingState) => {
      state.status = "failed";
      state.isError = true;
      state.isSuccess = false;
      state.isLoading = false;
    });
};

export const PaymentTrackingSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    /**
     * Clear all payment data and reset loading states
     */
    clearState: (state) => {
      state.activePaymentDetails = null;
      state.closePaymentDetails = null;
      state.defaultPaymentDetails = null;
      state.amountDueAnalysisReport = null;
      state.skuAndDoiReport = null;
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
    },

    /**
     * Reset to initial state
     */
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    // All thunks follow identical pattern - use helper to eliminate duplication
    addAsyncThunkCases(builder, getActivePayment, "activePaymentDetails");
    addAsyncThunkCases(builder, getClosePayment, "closePaymentDetails");
    addAsyncThunkCases(builder, getDefaultPayment, "defaultPaymentDetails");
    addAsyncThunkCases(
      builder,
      getAmountDueAnalysis,
      "amountDueAnalysisReport"
    );
    addAsyncThunkCases(builder, getSkuAndDoiSales, "skuAndDoiReport");
  },
});

export const { clearState, resetState } = PaymentTrackingSlice.actions;

export default PaymentTrackingSlice.reducer;
